/**
 * Pure formatting functions for job card labels.
 * Used by SSR to precompute labels so cards don't run Intl/Date/toLocaleString in render.
 * All logic is isolated and tested for consistency and edge cases.
 */

import { formatSalaryMinMaxPair, formatSalaryMinMaxParts } from './format-salary-min-max-pair';
import { formatEuroAmount } from '../location/format-monthly-salary';

/**
 * Formats "last seen" as a relative time string (e.g. "2 days ago") using Intl.RelativeTimeFormat.
 * Safe for SSR when called with a fixed reference time (e.g. request time).
 *
 * @param lastSeen - ISO date string or Unix timestamp (ms)
 * @param locale - BCP 47 locale (e.g. 'en', 'fi', 'sv')
 * @param nowMs - Reference time in ms (default: Date.now(); pass explicitly for SSR/test)
 * @returns Localized relative time string, or empty string if invalid
 */
export function formatLastSeenLabel(
  lastSeen: number | string | undefined,
  locale: string,
  nowMs?: number
): string {
  if (lastSeen === undefined || lastSeen === null) return '';
  const now = typeof nowMs === 'number' && Number.isFinite(nowMs) ? nowMs : Date.now();
  const parseTimestamp = (value: number | string): Date => {
    if (typeof value === 'number') {
      return new Date(value < 10_000_000_000 ? value * 1000 : value);
    }
    const raw = String(value).trim();
    if (/^\d+$/.test(raw)) {
      const numeric = Number(raw);
      if (Number.isFinite(numeric)) {
        return new Date(numeric < 10_000_000_000 ? numeric * 1000 : numeric);
      }
    }
    return new Date(raw);
  };
  const date = parseTimestamp(lastSeen);
  if (Number.isNaN(date.getTime())) return '';
  const diffMs = now - date.getTime();
  if (diffMs < 0) return ''; // future date

  const diffMins = Math.floor(diffMs / 60_000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  if (diffMins < 120) {
    return rtf.format(-diffMins, 'minute');
  }
  if (diffHours < 24) {
    return rtf.format(-diffHours, 'hour');
  }
  return rtf.format(-diffDays, 'day');
}

/**
 * One euro amount for job cards: "4 000 €" in fi/sv (formatEuroAmount, the euro sign after the
 * number as Finnish and Swedish write it), and the long-standing "€" + Intl grouping everywhere
 * else, so en output and any other caller's locale stay exactly as they were.
 */
function formatCardEuroAmount(value: number, locale: string): string {
  const lang = locale.toLowerCase().slice(0, 2);
  if (lang === 'fi' || lang === 'sv') return formatEuroAmount(value, lang);
  return `€${new Intl.NumberFormat(locale).format(value)}`;
}

/**
 * Formats a salary range for display: "€4,000 - €6,000" (en), "4 000 - 6 000 €" (fi, sv), or a
 * single amount when min === max. Amounts come from formatEuroAmount, so fi/sv put the euro sign
 * after the number the way Finnish and Swedish write it (shown once, after the range).
 *
 * @param min - Minimum salary (optional)
 * @param max - Maximum salary (optional)
 * @param locale - BCP 47 locale for number formatting (e.g. 'en', 'fi', 'sv')
 * @returns Formatted range string, or empty string if no valid range (caller shows "Salary not specified")
 */
export function formatSalaryLabel(
  min: number | undefined,
  max: number | undefined,
  locale?: string
): string {
  const hasMin = typeof min === 'number' && Number.isFinite(min) && min > 0;
  const hasMax = typeof max === 'number' && Number.isFinite(max) && max > 0;
  if (!hasMin || !hasMax) return '';
  const loc = locale || 'en';
  if (min === max) return formatCardEuroAmount(min, loc);
  const [low, high] = collapseRepeatedCurrency(formatCardEuroAmount(min, loc), formatCardEuroAmount(max, loc), {
    leading: false,
  });
  return formatSalaryMinMaxPair(min, max, (n) => (n === min ? low : high));
}

/**
 * Salary amounts for the SRP job card's brand panel, split so each figure can be rendered
 * with `white-space: nowrap` (see formatSalaryMinMaxParts). Unlike formatSrpSalaryDisplay,
 * this never prepends "Estimated"/"Arvioitu"/"Uppskattat" — the card shows that separately
 * via its existing "Monthly" / "Est. Monthly" caption, so baking it into the figure too was
 * saying the same thing twice.
 *
 * @returns [] when there's no valid range (caller shows "Salary not specified"), one amount
 * when min === max, otherwise [min, max].
 */
export function formatSrpSalaryParts(args: {
  salaryRangeMin?: number | null;
  salaryRangeMax?: number | null;
  locale?: string;
}): string[] {
  const min = args.salaryRangeMin ?? undefined;
  const max = args.salaryRangeMax ?? undefined;
  const hasMin = typeof min === 'number' && Number.isFinite(min) && min > 0;
  const hasMax = typeof max === 'number' && Number.isFinite(max) && max > 0;
  if (!hasMin || !hasMax) return [];
  const loc = args.locale || 'en';
  return formatSalaryMinMaxParts(min, max, (n) => formatCardEuroAmount(n, loc));
}

/**
 * Drops the currency sign that a two-amount range would otherwise repeat: the second amount's
 * leading sign ("€6,000", "8,000") or the first amount's trailing one ("6 000", "8 000 €"), so a
 * range reads "€6,000–8,000" in en and "6 000–8 000 €" in fi/sv.
 */
function collapseRepeatedCurrency(low: string, high: string, { leading = true } = {}): [string, string] {
  const lead = /^[^\d\s]+/.exec(low)?.[0];
  if (leading && lead && high.startsWith(lead)) return [low, high.slice(lead.length)];
  const trail = /[\s\u00a0\u202f]*[^\d\s\u00a0\u202f]+$/.exec(low)?.[0];
  const trailSign = trail?.trim();
  if (trail && trailSign && high.endsWith(trailSign)) return [low.slice(0, -trail.length), high];
  return [low, high];
}

/**
 * Joins formatSrpSalaryParts' output the way HitCardB2/HitTableB2 display it: an en dash between
 * the two amounts, with the currency sign shown once ("€6,000–8,000" in en, "6 000–8 000 €" in
 * fi/sv). Single source for this so every SRP surface stays in sync (a card row and a table row
 * disagreeing on formatting reads as a bug).
 */
export function joinSrpSalaryParts(parts: string[]): string {
  if (parts.length !== 2) return parts.join('–');
  return collapseRepeatedCurrency(parts[0], parts[1]).join('–');
}

function estimatePrefixForSrp(locale: string | undefined): string {
  const l = (locale ?? 'en').toLowerCase();
  if (l.startsWith('fi')) return 'Arvioitu';
  if (l.startsWith('sv')) return 'Uppskattat';
  return 'Estimated';
}

/**
 * Salary line for search-result cards (SRP, map pills, etc.).
 * When pay is filled from aggregated market stats (`salaryIsEstimate`), prefix the range
 * so it is not mistaken for an employer-stated salary.
 */
export function formatSrpSalaryDisplay(args: {
  salaryRangeMin?: number | null;
  salaryRangeMax?: number | null;
  salaryIsEstimate?: boolean | null;
  locale?: string;
}): string {
  const range = formatSalaryLabel(
    args.salaryRangeMin ?? undefined,
    args.salaryRangeMax ?? undefined,
    args.locale,
  );
  if (!range) return '';
  if (args.salaryIsEstimate === true) {
    return `${estimatePrefixForSrp(args.locale)} ${range}`;
  }
  return range;
}

/**
 * Enriches an array of hit objects with precomputed _lastSeenLabel and _salaryLabel.
 * Used by SSR so job cards can render without running Intl/Date in the browser.
 *
 * @param hits - Raw Algolia hits (mutated in place with added fields)
 * @param locale - BCP 47 locale
 * @param nowMs - Reference time in ms (e.g. Date.now() at request start)
 */
export function enrichHitsWithCardLabels(
  hits: Array<Record<string, unknown>>,
  locale: string,
  nowMs: number
): void {
  for (const hit of hits) {
    const lastSeen = hit.lastSeen as number | string | undefined;
    const min = hit.salaryRangeMin as number | undefined;
    const max = hit.salaryRangeMax as number | undefined;
    const salaryIsEstimate = hit.salaryIsEstimate as boolean | undefined;
    const lastSeenLabel = formatLastSeenLabel(lastSeen, locale, nowMs);
    const salaryLabel = formatSrpSalaryDisplay({
      salaryRangeMin: min,
      salaryRangeMax: max,
      salaryIsEstimate,
      locale,
    });
    if (lastSeenLabel) (hit as Record<string, unknown>)._lastSeenLabel = lastSeenLabel;
    if (salaryLabel) (hit as Record<string, unknown>)._salaryLabel = salaryLabel;
  }
}
